import React from 'react';
import { FileSpreadsheet, ArrowRight, Cpu, Package, Network, CheckSquare, Layers, ClipboardCheck } from 'lucide-react';
import { useDbNavigate } from '../../context/DatabaseContext';

const reportGroups = [
  {
    id: 'general',
    label: 'General',
    icon: Cpu,
    reports: [
      {
        slug: 'slugRptStd-Gen-IO_Assignments',
        label: 'I/O Assignments',
        description: 'Lists every tag\'s I/O assignment details — panel, drop, slot, and channel. Used to verify wiring assignments and cross-reference physical panel layout against the tag database.'
      },
      {
        slug: 'slugRptStd-Gen-Drawing_Log',
        label: 'Drawing Log',
        description: 'Shows the full revision history for drawings, filterable by source and drawing name. Used to track when drawings were revised, what changed, and when they were last plotted.'
      },
      {
        slug: 'slugRptStd-Gen-Panel_Report',
        label: 'Panel Report',
        description: 'Groups tag I/O assignments by panel, drop, slot, and channel. Organized around the panel hierarchy rather than the tag list — useful for panel-by-panel field verification.'
      }
    ]
  },
  {
    id: 'submittals',
    label: 'Submittals',
    icon: Package,
    reports: [
      {
        slug: 'slugRptStd-Sub-Index_Report_Panels',
        label: 'Index Report - Panels',
        description: 'Outputs index sheet data organized by panel. Used to generate the panel index pages that appear at the front of drawing packages, listing all panels and their contents.'
      },
      {
        slug: 'slugRptStd-Sub-Index_Report_Tags',
        label: 'Index Report - Tags',
        description: 'Outputs index sheet data organized by tag. Used to generate the tag index pages in drawing packages, listing all instrumentation tags and associated drawing references.'
      }
    ]
  },
  {
    id: 'networking',
    label: 'Networking',
    icon: Network,
    reports: [
      {
        slug: 'slugRptStd-Net-IP_Addresses_Device_Level',
        label: 'IP Addresses - Device Level',
        description: 'Lists all IP address assignments on the Dynamic LAN (DLN). Used to audit network addressing, identify conflicts, and document device IP allocations on the unsecured network segment.'
      },
      {
        slug: 'slugRptStd-Net-IP_Addresses_Site_Level',
        label: 'IP Addresses - Site Level',
        description: 'Lists all IP address assignments on the Secure LAN (SLN). Used to audit network addressing and document device IP allocations on the secure/isolated control network segment.'
      },
      {
        slug: 'slugRptStd-Net-Network_Connections',
        label: 'Network Connection Report',
        description: 'Shows ENET network connections by equipment, panel, and port. Used to document physical Ethernet topology — what device is plugged into which switch port on which panel.'
      }
    ]
  },
  {
    id: 'quality',
    label: 'Quality',
    icon: ClipboardCheck,
    reports: [
      {
        slug: 'oat-commissioning-keys',
        label: 'OAT Commissioning Keys',
        description: 'Generates the QD-OAT-101 Operational Acceptance Testing Procedures document from Commissioning Key records. Optionally includes associated tags per procedure for field use.',
        customRoute: true
      }
    ]
  },
  {
    id: 'commissioning',
    label: 'Commissioning',
    icon: CheckSquare,
    reports: [
      {
        slug: 'slugRptStd-Cx-OAT_Report',
        label: 'OAT Report',
        description: 'Shows Owner Acceptance Testing (OAT) status for each tag. Used during commissioning handoff to track which tags have been witnessed and accepted by the owner.'
      },
      {
        slug: 'slugRptStd-Cx-Panel_QAQC_Report',
        label: 'QAQC Report',
        description: 'Shows construction QA/QC status by tag. Used during the construction phase to track field verification — loop checks, continuity, instrument calibration, etc.'
      },
      {
        slug: 'slugRptStd-Cx-Commissioning_Status',
        label: 'Commissioning Status Report',
        description: 'Combined view of construction QA/QC and OAT status for every tag. The single-source-of-truth report for overall commissioning progress across both phases.'
      }
    ]
  },
  {
    id: 'dynamic',
    label: 'Dynamic',
    icon: Layers,
    reports: [
      {
        slug: 'slugRptDyn-Engineering_Data_Entry',
        label: 'Engineering and Data Entry Dynamic Report',
        description: 'Dynamic report across engineering and data entry modules.'
      }
    ]
  },
];

const ReportsPage = () => {
  const navigate = useDbNavigate();
  const totalReports = reportGroups.reduce((sum, g) => sum + g.reports.length, 0);

  return (
    <div className="stui-dashboard-body">
      <div className="stui-dashboard-header">
        <FileSpreadsheet size={20} className="stui-dashboard-header-icon" />
        <h1 className="stui-dashboard-header-title">Reports</h1>
        <span className="stui-dashboard-header-subtitle">{totalReports} reports available</span>
      </div>

      <div className="stui-dashboard-groups">
        {reportGroups.map(group => {
          const GroupIcon = group.icon;
          return (
            <div key={group.id}>
              <div className="stui-dashboard-group-header">
                <GroupIcon size={16} className="stui-dashboard-header-icon" />
                <span className="stui-dashboard-group-title">{group.label}</span>
                <span className="stui-dashboard-group-count">
                  {group.reports.length} {group.reports.length === 1 ? 'report' : 'reports'}
                </span>
              </div>
              <div className="stui-dashboard-tile-grid">
                {group.reports.map(report => (
                  <div
                    key={report.slug}
                    className="stui-tile stui-clickable"
                    onClick={() => navigate(`/reports/${report.slug}`)}
                  >
                    <div className="stui-tile-header">
                      <span className="stui-tile-title">{report.label}</span>
                      <ArrowRight size={16} className="stui-dashboard-tile-action" />
                    </div>
                    <div className="stui-dashboard-tile-content">
                      <p className="stui-dashboard-tile-description">{report.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportsPage;
